import { type ReactNode } from 'react';
import { BottomSheet } from './BottomSheet';
import { Button, type ButtonProps } from '../primitives/Button';
import { cn } from '@shared/lib/cn';

/**
 * iOS-style action sheet — stack of choices presented in a bottom-anchored
 * sheet with a Cancel button visually separated. Used for selecting from
 * lists where a full <Select> menu would feel desktop-y on mobile.
 */

export interface ActionSheetAction {
  label: string;
  onSelect: () => void | Promise<void>;
  variant?: ButtonProps['variant'];
  /** Render a custom prefix (e.g. icon) before the label. */
  leading?: ReactNode;
}

interface ActionSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title?: string;
  description?: string;
  actions: ActionSheetAction[];
  /** Cancel button label. Pass `null` to omit. */
  cancelLabel?: string | null;
}

export function ActionSheet({
  open,
  onOpenChange,
  title,
  description,
  actions,
  cancelLabel = 'Cancel',
}: ActionSheetProps): JSX.Element {
  const handleSelect = async (action: ActionSheetAction) => {
    await action.onSelect();
    onOpenChange(false);
  };

  return (
    <BottomSheet open={open} onOpenChange={onOpenChange} title={title} description={description}>
      <div className="flex flex-col gap-2 pb-2">
        {actions.map((action) => (
          <Button
            key={action.label}
            variant={action.variant ?? 'secondary'}
            size="lg"
            width="full"
            onClick={() => void handleSelect(action)}
            className={cn('justify-center', action.leading && 'gap-3')}
          >
            {action.leading}
            {action.label}
          </Button>
        ))}
        {cancelLabel ? (
          <div className="mt-2">
            <Button
              variant="ghost"
              size="lg"
              width="full"
              onClick={() => onOpenChange(false)}
              className="justify-center"
            >
              {cancelLabel}
            </Button>
          </div>
        ) : null}
      </div>
    </BottomSheet>
  );
}
